// Slideshow Express text-overlay model, SHARED between the server compositor
// (server/services/slideshow-text-overlay.ts) and the client's slide text
// editor (client/src/pages/slideshow-express.tsx).
//
// The per-visual-style typography below is authored from the Visual Style
// Templates doc's "Text overlay description" for each style. The client uses
// it to prefill the editor with the style's effective defaults; the server
// uses it to render the SVG overlay. Users can override text, font, color,
// size and alignment PER SLIDE — overrides are stored on
// slideshowExpressSlides.overlayJson and merged over the style at composite
// time.

import { z } from "zod";
import {
  getSlideshowBundledFont,
  type SlideshowTextSettings,
} from "./slideshow-text-settings";

// ── Style model ──────────────────────────────────────────────────────────────
export interface SlideTextStyle {
  /** SVG font-family stack (first entry must resolve via fontconfig). */
  fontFamily: string;
  /** SVG font-weight. */
  fontWeight: "normal" | "bold";
  /** Text fill color (from the doc). */
  color: string;
  /** Font size in px on a 1080-wide slide (doc range midpoint); scaled by W/1080. */
  sizePx: number;
  /** Line height as a multiple of font size. */
  lineHeight: number;
  /** Extra letter spacing in em (0 = none). */
  letterSpacingEm: number;
  /** Approximate average glyph advance as a fraction of font size — used for
   *  word-wrap since librsvg exposes no text measurement. Per family/weight. */
  avgAdvance: number;
  /** Vertical placement of the text block (from the doc's Placement rule). */
  placement: "center" | "belowCenter" | "upper" | "upperThird";
  /** Drop-shadow color (doc finish; black unless the doc names a warm tone). */
  shadowColor: string;
  /** Drop-shadow opacity; 0 disables the shadow entirely (doc: "No shadow"). */
  shadowOpacity: number;
  /** Optional outline color for the letter stroke. When omitted, a contrasting
   *  color is derived from `color` so the outline reads on any background. */
  strokeColor?: string;
  /** Optional stroke width as a fraction of font size. Defaults to
   *  DEFAULT_STROKE_WIDTH_EM when omitted; set to 0 to disable the outline. */
  strokeWidthEm?: number;
}

export const DEFAULT_TEXT_STYLE: SlideTextStyle = {
  fontFamily: "DejaVu Sans, sans-serif",
  fontWeight: "bold",
  color: "#ffffff",
  sizePx: 43,
  lineHeight: 1.3,
  letterSpacingEm: 0,
  avgAdvance: 0.56,
  placement: "center",
  shadowColor: "#000000",
  shadowOpacity: 0.5,
};

/** Per-visual-style typography, authored from the Visual Style Templates doc's
 *  "Text overlay description" for each style. Keys are
 *  `slideshowExpressRuns.styleId`. Sizes are the doc's px range midpoint on a
 *  1080x1920 slide. */
export const STYLE_TEXT_OVERLAYS: Record<string, SlideTextStyle> = {
  // Cormorant Garamond Semibold · warm ivory #F7EFE2 · 84–112px · centered or
  // slightly below center, generous letter-spacing feel · soft warm shadow.
  western_nostalgic_serif: {
    fontFamily: "Cormorant Garamond SemiBold, Cormorant Garamond, serif",
    fontWeight: "normal",
    color: "#F7EFE2",
    sizePx: 98,
    lineHeight: 1.35,
    letterSpacingEm: 0.02,
    avgAdvance: 0.44,
    placement: "belowCenter",
    shadowColor: "#4A3218",
    shadowOpacity: 0.45,
  },
  // Helvetica Neue / SF Pro Medium (Inter Medium is the bundled equivalent) ·
  // plain white · 28–42px · dead center · tiny black shadow at 20–30%.
  messy_camera_roll: {
    fontFamily: "Inter Medium, Inter, sans-serif",
    fontWeight: "normal",
    color: "#FFFFFF",
    sizePx: 36,
    lineHeight: 1.35,
    letterSpacingEm: 0,
    avgAdvance: 0.53,
    placement: "center",
    shadowColor: "#000000",
    shadowOpacity: 0.25,
  },
  // Didot / Canela / Cormorant Garamond Light · soft charcoal #23201C ·
  // 48–68px · elegant spacing, fewer lines · NO shadow (printed-in feel).
  clean_editorial_minimal: {
    fontFamily: "Cormorant Garamond Light, Cormorant Garamond, serif",
    fontWeight: "normal",
    color: "#23201C",
    sizePx: 58,
    lineHeight: 1.55,
    letterSpacingEm: 0.04,
    avgAdvance: 0.44,
    placement: "center",
    shadowColor: "#000000",
    shadowOpacity: 0,
  },
  // Georgia Bold / Baskerville Semibold / Libre Baskerville Bold · cream white
  // #FFF0D2 · 62–86px · upper-middle 2-line lockup · subtle dark brown shadow.
  human_craft_documentary: {
    fontFamily: "Libre Baskerville, serif",
    fontWeight: "bold",
    color: "#FFF0D2",
    sizePx: 74,
    lineHeight: 1.4,
    letterSpacingEm: 0,
    avgAdvance: 0.58,
    placement: "upper",
    shadowColor: "#3B2A1A",
    shadowOpacity: 0.5,
  },
  // Courier Prime / IBM Plex Mono · off-white #F8F4EA · 36–52px · upper third,
  // typewriter spacing · slight shadow over photo texture.
  research_desk_closeup: {
    fontFamily: "Courier Prime, monospace",
    fontWeight: "normal",
    color: "#F8F4EA",
    sizePx: 44,
    lineHeight: 1.45,
    letterSpacingEm: 0,
    avgAdvance: 0.6,
    placement: "upperThird",
    shadowColor: "#000000",
    shadowOpacity: 0.35,
  },
  // EB Garamond Medium / Georgia · warm cream #FFF4E3 · 52–76px · centered calm
  // 2-line phrasing · very soft brown shadow at low opacity.
  cozy_home_film: {
    fontFamily: "EB Garamond Medium, EB Garamond, serif",
    fontWeight: "normal",
    color: "#FFF4E3",
    sizePx: 64,
    lineHeight: 1.5,
    letterSpacingEm: 0.01,
    avgAdvance: 0.48,
    placement: "center",
    shadowColor: "#3D2B1A",
    shadowOpacity: 0.35,
  },
  // Anton / Impact / Bebas Neue · bright white · 58–82px · centered, one strong
  // line preferred · heavy black shadow for flash-photo contrast.
  dark_flash_confessional: {
    fontFamily: "Anton, sans-serif",
    fontWeight: "normal",
    color: "#FFFFFF",
    sizePx: 70,
    lineHeight: 1.25,
    letterSpacingEm: 0.01,
    avgAdvance: 0.5,
    placement: "center",
    shadowColor: "#000000",
    shadowOpacity: 0.85,
  },
  // Cormorant Garamond Regular (script accents like Great Vibes are bundled but
  // not mixed in — single-family lockup) · dusty rose #A98272 · 46–64px ·
  // upper-center over bright fabric space, airy line breaks · NO shadow.
  soft_wellness_journal: {
    fontFamily: "Cormorant Garamond, serif",
    fontWeight: "normal",
    color: "#A98272",
    sizePx: 55,
    lineHeight: 1.6,
    letterSpacingEm: 0.03,
    avgAdvance: 0.44,
    placement: "upper",
    shadowColor: "#000000",
    shadowOpacity: 0,
  },
  // Adobe Garamond / Georgia / Baskerville (EB Garamond is the bundled
  // equivalent) · soft warm white #FAF3E6 · 64–90px · centered 2-line
  // sentimental lockup · light golden-brown shadow.
  sunlit_rural_ritual: {
    fontFamily: "EB Garamond, serif",
    fontWeight: "normal",
    color: "#FAF3E6",
    sizePx: 77,
    lineHeight: 1.45,
    letterSpacingEm: 0.02,
    avgAdvance: 0.48,
    placement: "center",
    shadowColor: "#7A5B2E",
    shadowOpacity: 0.4,
  },
  // Recoleta / Cooper / Georgia Semibold (Playfair Display SemiBold is the
  // bundled equivalent) · cream paper ink #F3E6C8 · 58–82px · centered stacked
  // title-card style · soft shadow in matching warm ink.
  vintage_scrapbook_collage: {
    fontFamily: "Playfair Display SemiBold, Playfair Display, serif",
    fontWeight: "normal",
    color: "#F3E6C8",
    sizePx: 70,
    lineHeight: 1.4,
    letterSpacingEm: 0,
    avgAdvance: 0.54,
    placement: "center",
    shadowColor: "#3A2E1C",
    shadowOpacity: 0.45,
  },
};

export function resolveSlideTextStyle(styleId: string | null | undefined): SlideTextStyle {
  return (styleId && STYLE_TEXT_OVERLAYS[styleId]) || DEFAULT_TEXT_STYLE;
}

// ── User-selectable overlay fonts ────────────────────────────────────────────
// Curated from the TTFs bundled in server/assets/fonts (installed into
// ~/.fonts at first composite) plus the DejaVu system default. `family` is the
// SVG font-family stack the compositor renders with; `avgAdvance` feeds the
// word-wrap estimate (librsvg has no text measurement API).
export interface SlideOverlayFont {
  key: string;
  label: string;
  family: string;
  fontWeight: "normal" | "bold";
  avgAdvance: number;
}

export const SLIDESHOW_OVERLAY_FONTS: SlideOverlayFont[] = [
  { key: "inter", label: "Inter — clean sans", family: "Inter Medium, Inter, sans-serif", fontWeight: "normal", avgAdvance: 0.53 },
  { key: "bold-sans", label: "DejaVu Sans — bold sans", family: "DejaVu Sans, sans-serif", fontWeight: "bold", avgAdvance: 0.56 },
  { key: "anton", label: "Anton — bold impact", family: "Anton, sans-serif", fontWeight: "normal", avgAdvance: 0.5 },
  { key: "cormorant", label: "Cormorant Garamond — elegant serif", family: "Cormorant Garamond, serif", fontWeight: "normal", avgAdvance: 0.44 },
  { key: "cormorant-semibold", label: "Cormorant Garamond SemiBold", family: "Cormorant Garamond SemiBold, Cormorant Garamond, serif", fontWeight: "normal", avgAdvance: 0.44 },
  { key: "eb-garamond", label: "EB Garamond — classic serif", family: "EB Garamond, serif", fontWeight: "normal", avgAdvance: 0.48 },
  { key: "libre-baskerville", label: "Libre Baskerville — literary serif", family: "Libre Baskerville, serif", fontWeight: "normal", avgAdvance: 0.58 },
  { key: "libre-baskerville-bold", label: "Libre Baskerville Bold", family: "Libre Baskerville, serif", fontWeight: "bold", avgAdvance: 0.6 },
  { key: "playfair", label: "Playfair Display — editorial serif", family: "Playfair Display SemiBold, Playfair Display, serif", fontWeight: "normal", avgAdvance: 0.54 },
  { key: "courier", label: "Courier Prime — typewriter", family: "Courier Prime, monospace", fontWeight: "normal", avgAdvance: 0.6 },
  { key: "great-vibes", label: "Great Vibes — script", family: "Great Vibes, cursive", fontWeight: "normal", avgAdvance: 0.4 },
];

export function findOverlayFont(key: string | null | undefined): SlideOverlayFont | undefined {
  return key ? SLIDESHOW_OVERLAY_FONTS.find((f) => f.key === key) : undefined;
}

/** Best-effort reverse lookup: which selectable font matches a style's family
 *  stack (used by the client to show the style default in the font picker). */
export function overlayFontKeyForFamily(family: string): string | undefined {
  return SLIDESHOW_OVERLAY_FONTS.find((f) => f.family === family)?.key;
}

// ── Per-slide user overrides ─────────────────────────────────────────────────
// All fields optional: an absent field falls back to the visual style's
// default. Stored on slideshowExpressSlides.overlayJson.
export const slideOverlayOverridesSchema = z.object({
  /** Selectable font (key into SLIDESHOW_OVERLAY_FONTS). */
  fontKey: z.string().trim().min(1).max(60).optional(),
  /** Text fill color as #rrggbb. */
  color: z.string().trim().regex(/^#[0-9a-fA-F]{6}$/, "Color must be a hex value like #ffffff").optional(),
  /** Font size in px on a 1080-wide slide (scaled to the actual image). */
  sizePx: z.coerce.number().int().min(16).max(160).optional(),
  /** Horizontal alignment of the text block. */
  align: z.enum(["left", "center", "right"]).optional(),
  /** Vertical position of the text block. */
  vertical: z.enum(["top", "middle", "bottom"]).optional(),
  /** Free-form drag position: the text block's ANCHOR X as a fraction of the
   *  slide width (0 = left edge, 1 = right edge). Only takes effect when posY
   *  is also present; then position wins over the align/vertical presets for
   *  placement (align still controls multi-line text alignment). */
  posX: z.coerce.number().min(0).max(1).optional(),
  /** Free-form drag position: the text block's vertical CENTER as a fraction
   *  of the slide height (0 = top, 1 = bottom). Pairs with posX. */
  posY: z.coerce.number().min(0).max(1).optional(),
}).strict().superRefine((value, ctx) => {
  // Coordinates are a normalized pair. A half-position is ambiguous and must
  // never silently fall back to a preset during a durable revision.
  if ((value.posX == null) !== (value.posY == null)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [value.posX == null ? "posX" : "posY"], message: "posX and posY must be provided together as normalized 0..1 coordinates" });
  }
  if (value.fontKey && !findOverlayFont(value.fontKey)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["fontKey"], message: "fontKey must be one of the supported Slideshow overlay fonts" });
  }
});

export type SlideOverlayOverrides = z.infer<typeof slideOverlayOverridesSchema>;

/** Defensive parse for overlayJson read back from the DB (worker/composite
 *  paths). Unknown/invalid payloads degrade to "no overrides" rather than
 *  failing a render. */
export function parseSlideOverlayOverrides(raw: unknown): SlideOverlayOverrides | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const parsed = slideOverlayOverridesSchema.safeParse(raw);
  if (!parsed.success) return undefined;
  return Object.keys(parsed.data).length > 0 ? parsed.data : undefined;
}

/** True when the overrides carry a complete free-form drag position. */
export function hasOverlayPosition(
  overrides: SlideOverlayOverrides | null | undefined,
): overrides is SlideOverlayOverrides & { posX: number; posY: number } {
  return typeof overrides?.posX === "number" && typeof overrides?.posY === "number";
}

// ── Text layout (shared by server compositor + client drag preview) ─────────
// The exact wrap/size math the server uses when rasterizing the overlay. The
// client renders its draggable placement preview through the SAME functions so
// what the user drags matches what sharp composites.

/** Strip the wrapping quotation marks the copy agent tends to add. The overlay
 *  must NEVER show surrounding quotes. Interior quotes (a real apostrophe or a
 *  quoted word inside the sentence) are preserved. */
export function cleanOverlayText(raw: string | null | undefined): string {
  let t = (raw ?? "").toString().replace(/\r/g, " ").replace(/\s+/g, " ").trim();
  if (!t) return "";
  // Unescape common escaped quotes first (e.g. a JSON-ish \" that leaked through).
  t = t.replace(/\\"/g, '"').replace(/\\'/g, "'").trim();
  const pairs: Array<[string, string]> = [
    ['"', '"'],
    ["'", "'"],
    ["\u201C", "\u201D"], // “ ”
    ["\u2018", "\u2019"], // ‘ ’
    ["\u00AB", "\u00BB"], // « »
  ];
  let changed = true;
  while (changed && t.length >= 2) {
    changed = false;
    for (const [open, close] of pairs) {
      if (t.length > open.length + close.length - 1 && t.startsWith(open) && t.endsWith(close)) {
        t = t.slice(open.length, t.length - close.length).trim();
        changed = true;
        break;
      }
    }
  }
  return t.trim();
}

/** Greedy word-wrap by an approximate character budget per line. A single word
 *  longer than the budget is kept on its own line rather than split. */
export function wrapOverlayText(text: string, maxChars: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = "";
  for (const w of words) {
    const candidate = cur ? `${cur} ${w}` : w;
    if (!cur || candidate.length <= maxChars) {
      cur = candidate;
    } else {
      lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

export interface SlideTextLayoutInput {
  /** Already-cleaned overlay text (run through cleanOverlayText). */
  text: string;
  /** Actual image width in px. */
  W: number;
  /** Style/override font size specified on a 1080-wide slide. */
  sizePx: number;
  /** Line height as a multiple of font size. */
  lineHeight: number;
  /** Extra letter spacing in em. */
  letterSpacingEm: number;
  /** Approximate average glyph advance as a fraction of font size. */
  avgAdvance: number;
  /** Horizontal alignment (drives the wrap column width). */
  align: "left" | "center" | "right";
}

export interface SlideTextLayout {
  /** Final font size in px on the actual image. */
  fontSize: number;
  /** Wrapped lines. */
  lines: string[];
  /** Line height in px. */
  lineHeightPx: number;
  /** Total block height in px (lines × line height). */
  totalHeight: number;
  /** Wrap column width in px. */
  maxTextWidth: number;
}

/** Validates the same approximate glyph geometry used by layoutSlideText before
 * an irreversible durable revision is staged. Unlike the renderer's defensive
 * clamps, this rejects a request whose text would extend beyond the canvas. */
export function validateOverlayLayoutBounds(input: SlideTextLayoutInput & {
  H: number;
  anchorX: number;
  vertical?: "top" | "middle" | "bottom";
  position?: { x: number; y: number };
  placement: SlideTextStyle["placement"];
  placementExplicit?: boolean;
}): { ok: true } | { ok: false; reason: "vertical_overflow" | "horizontal_overflow"; line?: string } {
  const layout = layoutSlideText(input);
  if (layout.totalHeight > input.H * .9) return { ok: false, reason: "vertical_overflow" };
  // The compositor's SVG text is anchored at x. Measure using its same advance
  // approximation, including spacing, so a single unbroken word cannot escape
  // the wrapping column unnoticed.
  const advance = layout.fontSize * (input.avgAdvance + input.letterSpacingEm);
  for (const line of layout.lines) {
    const width = line.length * advance;
    const left = input.align === "left" ? input.anchorX : input.align === "right" ? input.anchorX - width : input.anchorX - width / 2;
    if (left < 0 || left + width > input.W) return { ok: false, reason: "horizontal_overflow", line };
  }
  return { ok: true };
}

/** Wrap + step-down sizing for the overlay text block. Prefers wrapping over
 *  shrinking: allows up to 6 lines at the style's full size before stepping
 *  the font down, so copy forms a taller compact block rather than a few long
 *  shrunken lines. */
export function layoutSlideText(input: SlideTextLayoutInput): SlideTextLayout {
  const { text, W, align } = input;
  // Text column: centered copy caps lines at 60% of the slide width so it
  // wraps into shorter, clearly centered lines; side-aligned copy gets a
  // little more room (72%) against an 8% edge margin.
  const maxTextWidth = align === "center" ? W * 0.6 : W * 0.72;
  // Doc sizes are specified on a 1080-wide slide; scale to the actual width.
  const scale = W / 1080;
  let fontSize = Math.round(input.sizePx * scale);
  const minFont = Math.max(Math.round(fontSize * 0.6), Math.round(24 * scale));
  // Letter spacing widens the effective advance for the wrap estimate.
  const advance = input.avgAdvance + input.letterSpacingEm;
  let lines: string[] = [];
  for (let i = 0; i < 10; i++) {
    const maxChars = Math.max(6, Math.floor(maxTextWidth / (fontSize * advance)));
    lines = wrapOverlayText(text, maxChars);
    if (lines.length <= 6 || fontSize <= minFont) break;
    fontSize = Math.round(fontSize * 0.9);
  }
  const lineHeightPx = Math.round(fontSize * input.lineHeight);
  const totalHeight = lines.length * lineHeightPx;
  return { fontSize, lines, lineHeightPx, totalHeight, maxTextWidth };
}

// ── Per-style admin settings (Manage Styles "Slideshow text") ────────────────
// Admin-saved settings are merged FIELD BY FIELD over the style's hardcoded
// defaults: any unset knob keeps the legacy value, so a style with no (or
// partial) settings renders exactly as before. Alignment and an EXPLICIT
// placement are new capabilities that only activate when set. Shared so the
// client's drag-placement hit region sits exactly where the server composites
// the text (admin-created styles previously diverged).

/** Default outline width (fraction of font size). With paint-order="stroke" the
 *  stroke is painted behind the fill, so roughly half of this shows as a clean
 *  outline around each glyph. */
export const DEFAULT_STROKE_WIDTH_EM = 0.08;

export interface ResolvedSlideTextStyle extends SlideTextStyle {
  /** Horizontal alignment. Absent/center = legacy centered rendering. */
  align?: "left" | "center" | "right";
  /** True when the admin explicitly chose a vertical placement — only then
   *  does `placement` actually move the block (legacy renders all use the
   *  shared 43%-center rule regardless of the style's placement field). */
  placementExplicit?: boolean;
  /** Uploaded custom font to install before rendering (R2/local URL). */
  customFontUrl?: string;
}

export function mergeSlideTextSettings(
  base: SlideTextStyle,
  settings: SlideshowTextSettings | null | undefined,
): ResolvedSlideTextStyle {
  if (!settings) return { ...base };
  const merged: ResolvedSlideTextStyle = { ...base };

  // Font: uploaded custom font wins over a bundled pick.
  if (settings.customFont?.url && settings.customFont.family) {
    merged.fontFamily = `${settings.customFont.family}, ${base.fontFamily}`;
    merged.customFontUrl = settings.customFont.url;
    // Unknown metrics for arbitrary uploads — use a middle-of-the-road advance
    // so the wrap estimate stays reasonable.
    merged.avgAdvance = 0.52;
    merged.fontWeight = "normal";
  } else if (settings.fontKey) {
    const bundled = getSlideshowBundledFont(settings.fontKey);
    if (bundled) {
      merged.fontFamily = bundled.fontFamily;
      merged.fontWeight = bundled.fontWeight;
      merged.avgAdvance = bundled.avgAdvance;
    }
  }

  if (settings.color) merged.color = settings.color;
  if (settings.sizePx != null) merged.sizePx = settings.sizePx;

  // Stroke: null = legacy behavior; false = off; true = on with overrides.
  if (settings.strokeEnabled === false) {
    merged.strokeWidthEm = 0;
  } else if (settings.strokeEnabled === true) {
    if (settings.strokeWidthPx != null && settings.strokeWidthPx > 0) {
      // Admin width is px on a 1080-wide slide; convert to an em fraction of
      // the resolved font size so the existing scale-by-width math applies.
      merged.strokeWidthEm = settings.strokeWidthPx / merged.sizePx;
    } else if (!(merged.strokeWidthEm && merged.strokeWidthEm > 0)) {
      merged.strokeWidthEm = DEFAULT_STROKE_WIDTH_EM;
    }
    if (settings.strokeColor) merged.strokeColor = settings.strokeColor;
  } else if (settings.strokeColor) {
    // Color set without touching the toggle: recolor the (default) stroke.
    merged.strokeColor = settings.strokeColor;
  }

  if (settings.align) merged.align = settings.align;
  if (settings.placement) {
    merged.placement = settings.placement;
    merged.placementExplicit = true;
  }
  return merged;
}

// Resolve the top edge of the text block. Legacy rule (no user vertical, no
// admin-explicit placement): the block's vertical center sits slightly above
// the image's center (43% of height). A per-slide USER vertical override wins:
// "top" starts the block just below the phone chrome; "bottom" ends it above
// the TikTok caption area; "middle" is the legacy 43% center. Otherwise, when
// the admin EXPLICITLY picked a placement in Manage Styles, the block's
// vertical center moves to the placement's anchor. Always clamped so the
// block stays fully inside the image with a 5% margin.
export const PLACEMENT_CENTER_FRACTION: Record<SlideTextStyle["placement"], number> = {
  upper: 0.2,
  upperThird: 0.33,
  center: 0.43,
  belowCenter: 0.58,
};

export function resolveOverlayBlockTop(opts: {
  vertical?: "top" | "middle" | "bottom";
  placement: SlideTextStyle["placement"];
  placementExplicit?: boolean;
  H: number;
  totalHeight: number;
}): number {
  const { vertical, placement, placementExplicit, H, totalHeight } = opts;
  let top: number;
  if (vertical === "top") {
    top = Math.round(H * 0.1);
  } else if (vertical === "bottom") {
    top = Math.round(H * 0.85 - totalHeight);
  } else if (vertical === "middle") {
    top = Math.round(H * 0.43 - totalHeight / 2);
  } else {
    const frac = placementExplicit ? (PLACEMENT_CENTER_FRACTION[placement] ?? 0.43) : 0.43;
    top = Math.round(H * frac - totalHeight / 2);
  }
  const minTop = Math.round(H * 0.05);
  const maxTop = Math.max(minTop, Math.round(H * 0.95 - totalHeight));
  return Math.min(Math.max(top, minTop), maxTop);
}

/** Clamp the text block's top edge so the block stays fully inside the image
 *  with a 5% margin (same rule for preset placements and drag positions). */
export function clampOverlayBlockTop(top: number, H: number, totalHeight: number): number {
  const minTop = Math.round(H * 0.05);
  const maxTop = Math.max(minTop, Math.round(H * 0.95 - totalHeight));
  return Math.min(Math.max(top, minTop), maxTop);
}

/** Clamp the drag anchor X so text can't be dropped fully off-canvas. */
export function clampOverlayAnchorX(anchorX: number, W: number): number {
  return Math.min(Math.max(anchorX, Math.round(W * 0.05)), Math.round(W * 0.95));
}
